import { useNavigate } from 'react-router';

import Crown from '@/assets/icons/Crown';
import Button from '@/components/Buttons/Button';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import { useExportMeta } from '@/features/DataHub/modules/Export/context/ExportMetaContext';
import { useExportPreview } from '@/features/DataHub/modules/Export/context/ExportPreviewContext';
import { useExportRequest } from '@/features/DataHub/modules/Export/context/ExportRequestContext';
import type { ButtonConfig } from '@/features/DataHub/types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { closeDrawer } from '@/slices/drawerSlice';

const DataFooterButtons = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isUserPremium, isUserExpired } = useUserDetails();
  const { exportApiRequestString, exportCurlRequestString, exportName } = useExportMeta();
  const { previewOptionId, previewLoading, previewDownloadFn, previewContentToCopy } =
    useExportPreview();
  const {
    handleExportData,
    exportDataWithParams,
    handleCopyToClipBoard,
    capturePosthogEvent,
    loading,
    setExcelExportStatus
  } = useExportRequest();

  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  const isIntegrationExport = exportOption?.preview?.type === 'integration';

  /** Python, Power Query, CSV, curl (json) etc. – content is in the preview; download = local file only, no API call */
  const isClientSideDownloadOnly =
    exportOption?.preview?.type === 'code' || exportOption?.preview?.type === 'simple';

  const shouldShowUpgradeButtons = !isUserPremium || isUserExpired;

  const buttons: ButtonConfig[] = shouldShowUpgradeButtons
    ? [{ label: 'Upgrade now', action: 'custom' }]
    : (exportOption?.buttons ?? []);

  const handleButtonClick = (button: ButtonConfig) => {
    if (button.action === 'copy') {
      const textToCopy = previewContentToCopy ?? exportCurlRequestString;
      handleCopyToClipBoard(textToCopy);
    } else if (button.action === 'download' || button.action === 'export') {
      capturePosthogEvent();
      const isExcel = exportOption?.id === 'excel';

      if (isIntegrationExport && exportOption?.id) {
        setExcelExportStatus('loading');
        exportDataWithParams({
          type: 'third-party',
          exportSubType: exportOption.id
        })
          .then(() => {
            setExcelExportStatus('queued');
          })
          .catch(() => {
            setExcelExportStatus('idle');
          });
        return;
      }

      // Content in preview (Python, Power Query, CSV): download the file, no API call
      if (isClientSideDownloadOnly && previewDownloadFn) {
        previewDownloadFn();
        return;
      }

      // Show loading modal for non-Excel exports (Excel handles its own loading state)
      if (!isExcel) {
        setExcelExportStatus('loading');
      }

      // First save to past exports, then download
      handleExportData()
        .then(() => {
          if (previewDownloadFn) {
            previewDownloadFn();
          }
          if (!isExcel) {
            setExcelExportStatus('idle');
          }
        })
        .catch(() => {
          if (!isExcel) {
            setExcelExportStatus('idle');
          }
        });
    } else if (button.action === 'launch') {
      capturePosthogEvent();
      if (exportApiRequestString) {
        window.open(exportApiRequestString, '_blank', 'noopener,noreferrer');
      }
    } else if (button.label === 'Upgrade now') {
      dispatch(closeDrawer());
      navigate('/manage-subscription');
    } else if (button.onClick) {
      capturePosthogEvent();
      button.onClick();
    }
  };

  return (
    <>
      {buttons.length === 1 && (
        <Button
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main'
          }}
          variant='outlined'
          onClick={() => {
            dispatch(closeDrawer());
          }}
        >
          Cancel
        </Button>
      )}
      {buttons.map(button => {
        const isExportAction =
          button.action === 'download' || button.action === 'export' || button.action === 'launch';
        const isNameInvalid = !exportName || exportName.trim() === '';
        const isClientSideDownload =
          (button.action === 'download' || button.action === 'export') && isClientSideDownloadOnly;
        const isDisabled =
          button.disabled ||
          (isExportAction &&
            (loading || previewLoading || (!isClientSideDownload && isNameInvalid)));

        return (
          <Button
            key={button.label}
            disabled={isDisabled}
            startIcon={button.label === 'Upgrade now' ? <Crown /> : undefined}
            variant={button.variant ?? 'contained'}
            onClick={() => {
              handleButtonClick(button);
            }}
          >
            {button.label}
          </Button>
        );
      })}
    </>
  );
};

export default DataFooterButtons;
